import { createContext, useContext } from 'react'
import type { SortingStrategy } from '@dnd-kit/sortable'

/**
 * What a drag may do in the Tables sidebar right now. A table moves into a
 * folder or to the top level under any sort; only Custom reorders (Name and
 * Recency decide the order themselves), so only Custom moves a table among
 * its neighbors, and moves folders at all (among their siblings, into another
 * folder, or to the top level).
 */
export interface TableListRules {
  /** Tables can be picked up: while not searching, with somewhere to go. */
  tableDragEnabled: boolean
  /** Rows reorder, and folders can be picked up: Custom order, while not searching. */
  reorderEnabled: boolean
  /** How many folders there are, so a lone one knows it has nowhere to go. */
  folderCount: number
  /**
   * While searching, folders holding matches show open no matter how they
   * were left; clicking one collapses it for this search only. `null` when
   * not searching (each folder's own open state applies).
   */
  searchExpansion: SearchExpansion | null
}

interface SearchExpansion {
  collapsedFolderIds: ReadonlySet<string>
  toggleFolder: (folderId: string) => void
}

/** Under Name or Recency, rows hold their places while a table is dragged. */
export const KEEP_ORDER_STRATEGY: SortingStrategy = () => null

const CUSTOM_TABLE_LIST_RULES: TableListRules = {
  tableDragEnabled: true,
  reorderEnabled: true,
  folderCount: 0,
  searchExpansion: null,
}

export const TableListRulesContext = createContext<TableListRules>(CUSTOM_TABLE_LIST_RULES)

export function useTableListRules(): TableListRules {
  return useContext(TableListRulesContext)
}
